import { userApiSlice, useUserInfoQuery } from "../../../api/userApiSlice";
import { useState } from "react";
import { useCreateLassoSafeUserMutation } from "../../../api/lassoSafeApiSlice";
import { LassoSafeInstance, LassoSafeState, LassoSafeType } from "../types";
import { CircularProgress } from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../app/store";

const useLassoSafeController = () => {
    const initialState: LassoSafeState = {
        type: LassoSafeType.DEFAULT,
        text: "To access the Sandbox, a first time setup of the account is required. This will only take a few seconds, would you like to proceed?",
        button: {
            text: "Create my environment now",
            action: () => createUser()
        }
    }

    const dispatch = useDispatch<AppDispatch>();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [lassoSafeState, setLassoSafeState] = useState<LassoSafeState>(initialState);
    const [createLassoSafeUser] = useCreateLassoSafeUserMutation()
    const { data: userData } = useUserInfoQuery({});

    const lassoSafeLink = window.env.openOnDemandURL;
    const lassoSafeOnPremiseLink = window.env.openOnDemandOnPremiseURL;

    const openLassoSafe = (instance:LassoSafeInstance) => {
        if (!userData.lassosafeUsername && instance == LassoSafeInstance.CLOUD) {
            return setIsModalOpen(true);
        }
        navigateToLassoSafe(instance);
    }

    const openLassoOnPremise = ()=>{
        return openLassoSafe(LassoSafeInstance.ON_PREMISE);
    }

    const openLassoSafeOnCloud = ()=>{
        return openLassoSafe(LassoSafeInstance.CLOUD);
    }

    const createUser = async () => {
        setLassoSafeState({
            type: LassoSafeType.LOADING,
            text: "Preparing your environment...",
            icon: <CircularProgress color={"warning"}/>,
        })

        try {
            const response = await createLassoSafeUser({
                usernames: [userData.username]
            })

            if (response.error) {
                setErrorState();
                console.error("Error trying create Sandbox User: ", response.error);
                return;
            }

            setLassoSafeState({
                type: LassoSafeType.SUCCESS,
                text: "Your environment is set up!",
                icon: <CheckCircleOutlineIcon color={"success"}/>,
                button: {
                    text: "Open Sandbox",
                    action: () => navigateToLassoSafe(LassoSafeInstance.CLOUD)
                }
            })
            dispatch(userApiSlice.util.updateQueryData('userInfo', {}, (draft) => {
                const user = response.data.results.find(respData => respData.username === draft.username);
                draft.lassosafeUsername = user?.username;
            }));
        } catch (error) {
            setErrorState();
            console.error("Error trying create Sandbox User: ", error);
        }
    }

    const closeModal = () => {
        setIsModalOpen(false);
    }

    const navigateToLassoSafe = (instance: LassoSafeInstance) => {
        setLassoSafeState(initialState);
        window.open(instance == LassoSafeInstance.ON_PREMISE ? lassoSafeOnPremiseLink:lassoSafeLink, "_blank");
        closeModal();
    }

    const setErrorState = () => {
        setLassoSafeState({
            type: LassoSafeType.ERROR,
            text: "An error occurred!",
            icon: <ErrorOutlineOutlinedIcon color={"error"}/>,
            button: {
                text: "Try Again",
                action: () => setLassoSafeState(initialState)
            }
        });
    }

    return {
        openLassoSafe,
        closeModal,
        isModalOpen,
        lassoSafeState,
        openLassoSafeOnCloud,
        openLassoOnPremise
    }
};

export default useLassoSafeController;